import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import hospitalInfo from "../constants/hospitalInfo";
import formatDate from "./formatDate";
import logoBase64 from "../constants/logoBase64";

const currency = (n) =>
    `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function numberToWords(num) {
    if (!num || isNaN(num) || num <= 0) return "Zero Rupees Only";
    const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const inWords = (n) => {
        let str = "";
        if (n >= 10000000) {
            str += inWords(Math.floor(n / 10000000)) + " Crore ";
            n %= 10000000;
        }
        if (n >= 100000) {
            str += inWords(Math.floor(n / 100000)) + " Lakh ";
            n %= 100000;
        }
        if (n >= 1000) {
            str += inWords(Math.floor(n / 1000)) + " Thousand ";
            n %= 1000;
        }
        if (n >= 100) {
            str += inWords(Math.floor(n / 100)) + " Hundred ";
            n %= 100;
        }
        if (n > 0) {
            if (n < 20) {
                str += a[n] + " ";
            } else {
                str += b[Math.floor(n / 10)] + (n % 10 > 0 ? " " + a[n % 10] : "") + " ";
            }
        }
        return str.trim();
    };

    const whole = Math.floor(num);
    const paise = Math.round((num - whole) * 100);
    let result = inWords(whole) + " Rupees";
    if (paise > 0) {
        result += " and " + inWords(paise) + " Paise";
    }
    return result + " Only";
}

// ============================================================================
// 1. NEW INVOICE DESIGN (EXACT MATCH TO HTML PREVIEW & UPLOADED FORMAT)
// ============================================================================

export function normalizeBillItems(bill = {}) {
    let rawItems = [];
    if (Array.isArray(bill.items) && bill.items.length > 0) {
        rawItems = bill.items;
    } else if (typeof bill.items === "string") {
        try {
            const parsed = JSON.parse(bill.items);
            if (Array.isArray(parsed) && parsed.length > 0) rawItems = parsed;
        } catch (e) {}
    } else if (Array.isArray(bill.treatments) && bill.treatments.length > 0) {
        rawItems = bill.treatments;
    } else if (Array.isArray(bill.services) && bill.services.length > 0) {
        rawItems = bill.services;
    }

    if (rawItems.length === 0) {
        let fallbackDesc = bill.treatment || bill.treatmentName || bill.service || bill.notes;
        if (!fallbackDesc && bill.patient && Array.isArray(bill.patient.visits) && bill.patient.visits.length > 0) {
            const lastVisit = bill.patient.visits[bill.patient.visits.length - 1];
            if (lastVisit && lastVisit.treatment) {
                fallbackDesc = lastVisit.treatment;
            }
        }
        if (!fallbackDesc) {
            fallbackDesc = bill.doctor ? `Consultation & Treatment (${bill.doctor})` : "Naturopathy Consultation & Treatment";
        }

        const splitTreatments = String(fallbackDesc).split(/,\s*|\n+/).map(s => s.trim()).filter(Boolean);
        if (splitTreatments.length > 1) {
            const total = Number(bill.totalAmount || 0);
            const eachPrice = Math.round((total / splitTreatments.length) * 100) / 100;
            return splitTreatments.map((t, idx) => ({
                description: t,
                quantity: 1,
                unitPrice: idx === splitTreatments.length - 1 ? (total - eachPrice * (splitTreatments.length - 1)) : eachPrice,
                total: idx === splitTreatments.length - 1 ? (total - eachPrice * (splitTreatments.length - 1)) : eachPrice
            }));
        }

        return [{
            description: fallbackDesc,
            quantity: 1,
            unitPrice: Number(bill.totalAmount || 0),
            total: Number(bill.totalAmount || 0)
        }];
    }

    return rawItems.map(item => {
        const desc = (
            item.description ||
            item.name ||
            item.treatment ||
            item.service ||
            item.title ||
            item.item ||
            item.particulars ||
            item.treatmentName ||
            bill.notes ||
            "Treatment / Consultation"
        ).trim();

        const qty = Number(item.quantity != null ? item.quantity : (item.sessions != null ? item.sessions : (item.qty != null ? item.qty : 1))) || 1;
        const unitPrice = Number(item.unitPrice != null ? item.unitPrice : (item.rate != null ? item.rate : (item.price != null ? item.price : 0))) || 0;
        const total = Number(item.amount != null ? item.amount : (item.total != null ? item.total : (qty * unitPrice))) || (qty * unitPrice);

        return {
            description: desc,
            quantity: qty,
            unitPrice: unitPrice || (qty > 0 && total > 0 ? total / qty : 0),
            total: total
        };
    });
}

export function getInvoiceHtml(bill = {}, patient = {}, type = "digital") {
    const patientObj = bill.patient || patient || {};
    const patientName = (patientObj.name || "-").toUpperCase();
    const patientId = patientObj.patientId || bill.patientId || "-";
    const age = patientObj.age ? `${patientObj.age}` : "-";
    const gender = (patientObj.gender || "-").toUpperCase();
    const phone = patientObj.phone || "-";
    const address = (patientObj.address || "-").replace(/\r?\n|\r/g, ", ").toUpperCase();
    const formattedDate = bill.createdAt ? formatDate(bill.createdAt) : new Date().toLocaleDateString("en-GB");

    const items = normalizeBillItems(bill);

    const subtotal = items.reduce((acc, it) => acc + Number(it.total || 0), 0);
    const discount = Number(bill.discount || 0);
    const totalAmount = Number(bill.totalAmount || subtotal);
    const amountPaid = Number(bill.amountPaid != null ? bill.amountPaid : totalAmount);
    const amountInWords = numberToWords(totalAmount);

    const paymentMethod = (bill.paymentMethod || "Cash").toUpperCase();
    let paymentStatus = (bill.status || "Paid").toUpperCase();
    if (paymentStatus === "PAID" || totalAmount <= amountPaid) {
        paymentStatus = "FULLY PAID";
    }

    const itemsRowsHtml = items.map(item => `
        <tr style="border-bottom: 1px solid #e5e7eb;">
            <td style="padding: 8px 12px; border-right: 1px solid #d1d5db; font-weight: 500; color: #1f2937;">
                ${item.description.toUpperCase()}
            </td>
            <td style="padding: 8px 12px; border-right: 1px solid #d1d5db; text-align: center; color: #374151;">
                ${item.quantity}
            </td>
            <td style="padding: 8px 12px; border-right: 1px solid #d1d5db; text-align: right; color: #374151;">
                ${currency(item.unitPrice)}
            </td>
            <td style="padding: 8px 12px; text-align: right; font-weight: 600; color: #111827;">
                ${currency(item.total)}
            </td>
        </tr>
    `).join("");

    const discountRowHtml = discount > 0 ? `
        <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
            <span style="font-weight: 600; color: #4b5563;">DISCOUNT</span>
            <span style="font-weight: 600; color: #dc2626;">- ${currency(discount)}</span>
        </div>
    ` : "";

    const signatureBlockHtml = type === "signature" ? `
        <div style="display: flex; justify-content: flex-end; margin-top: 55px; margin-bottom: 20px;">
            <div style="width: 200px; text-align: center;">
                <div style="height: 45px;"></div>
                <div style="border-bottom: 1px solid #6b7280; margin-bottom: 6px;"></div>
                <div style="font-size: 11.5px; font-weight: bold; color: #1f2937; letter-spacing: 0.3px;">Authorized Signatory</div>
            </div>
        </div>
    ` : "";

    const termsHtml = `
        <div style="font-size: 9.5px; color: #6b7280; line-height: 1.45; margin-bottom: 12px;">
            <strong style="color: #4b5563;">Terms & Conditions:</strong><br />
            1. All fees are non-refundable once treatments have commenced.<br />
            ${type === "signature" ? "2. Please retain this invoice for your medical records." : "2. This is an official digital invoice copy generated for your medical records."}
        </div>
    `;

    const htmlString = `
        <div style="width: 794px; min-height: 1123px; padding: 44px 48px; background: #ffffff; color: #111827; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; box-sizing: border-box; display: flex; flex-direction: column; justify-content: space-between;">
            <div>
                <!-- Top Header -->
                <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
                    <div>
                        <div style="font-family: 'Playfair Display', Georgia, serif; font-size: 48px; font-weight: bold; color: #4b8026; line-height: 1;">Invoice</div>
                    </div>
                    <div>
                        <img src="${logoBase64}" style="width: 4.5cm; height: 2.26cm; object-fit: contain; display: block;" alt="Logo" />
                    </div>
                </div>

                <!-- Two side-by-side Gray-capped cards -->
                <div style="display: flex; gap: 14px; margin-bottom: 22px;">
                    <!-- LEFT CARD: Patient Details -->
                    <div style="flex: 1; border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #f9fafb;">
                        <div style="background-color: #d1d5db; color: #1f2937; font-weight: bold; font-size: 11.5px; padding: 5px 12px; letter-spacing: 0.5px;">PATIENT DETAILS</div>
                        <div style="padding: 10px 12px; font-size: 11.5px; color: #374151; line-height: 1.6;">
                            <div style="font-weight: bold; color: #111827; font-size: 12.5px; margin-bottom: 2px;">${patientName}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">PATIENT ID:</span> ${patientId}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">AGE / GENDER:</span> ${age} / ${gender}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">PHONE:</span> ${phone}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">ADDRESS:</span> ${address}</div>
                        </div>
                    </div>

                    <!-- RIGHT CARD: Invoice Summary -->
                    <div style="flex: 1; border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; background: #f9fafb;">
                        <div style="background-color: #d1d5db; color: #1f2937; font-weight: bold; font-size: 11.5px; padding: 5px 12px; letter-spacing: 0.5px;">INVOICE SUMMARY</div>
                        <div style="padding: 10px 12px; font-size: 11.5px; color: #374151; line-height: 1.6;">
                            <div><span style="font-weight: 600; color: #6b7280;">INVOICE NO:</span> ${bill.invoiceNumber || "-"}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">INVOICE DATE:</span> ${formattedDate}</div>
                            <div><span style="font-weight: 600; color: #6b7280;">PAYMENT METHOD:</span> ${paymentMethod}</div>
                            <div style="display: flex; align-items: center; gap: 8px; margin-top: 2px;">
                                <span style="font-weight: 600; color: #6b7280;">PAYMENT STATUS:</span>
                                <span style="background-color: #73a742; color: #ffffff; padding: 1px 8px; border-radius: 3px; font-size: 10px; font-weight: bold;">${paymentStatus}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Treatment Table -->
                <div style="border: 1px solid #d1d5db; border-radius: 4px; overflow: hidden; margin-bottom: 22px;">
                    <table style="width: 100%; border-collapse: collapse; font-size: 11.5px;">
                        <thead>
                            <tr style="background-color: #e5e7eb; color: #1f2937; font-weight: bold; border-bottom: 1px solid #d1d5db;">
                                <th style="padding: 8px 12px; text-align: left; border-right: 1px solid #d1d5db;">DESCRIPTION</th>
                                <th style="padding: 8px 12px; text-align: center; width: 85px; border-right: 1px solid #d1d5db;">SESSIONS</th>
                                <th style="padding: 8px 12px; text-align: right; width: 115px; border-right: 1px solid #d1d5db;">UNIT PRICE</th>
                                <th style="padding: 8px 12px; text-align: right; width: 115px;">AMOUNT</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${itemsRowsHtml}
                        </tbody>
                    </table>
                </div>

                <!-- Financial Breakdown & Words -->
                <div style="display: flex; gap: 20px; align-items: flex-start; margin-bottom: 16px;">
                    <!-- Amount in words -->
                    <div style="flex: 1; padding: 12px 14px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px;">
                        <div style="font-size: 10px; font-weight: bold; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px;">TOTAL AMOUNT IN WORDS</div>
                        <div style="font-size: 12px; font-weight: 600; color: #1f2937; line-height: 1.4;">${amountInWords}</div>
                        <div style="font-size: 10px; color: #6b7280; margin-top: 8px;">
                            Payment Mode: <span style="font-weight: 600; color: #374151;">${paymentMethod}</span>
                        </div>
                    </div>

                    <!-- Totals box -->
                    <div style="width: 250px; font-size: 11.5px;">
                        <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
                            <span style="font-weight: 600; color: #4b5563;">SUBTOTAL</span>
                            <span style="font-weight: 600; color: #1f2937;">${currency(subtotal)}</span>
                        </div>
                        ${discountRowHtml}
                        <div style="display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 2px solid #73a742;">
                            <span style="font-size: 14px; font-weight: bold; color: #4b8026;">TOTAL AMOUNT</span>
                            <span style="font-size: 14px; font-weight: bold; color: #4b8026;">${currency(totalAmount)}</span>
                        </div>
                        <div style="display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #e5e7eb;">
                            <span style="font-weight: 600; color: #4b5563;">AMOUNT PAID</span>
                            <span style="font-weight: 600; color: #1f2937;">${currency(amountPaid)}</span>
                        </div>
                    </div>
                </div>

                <!-- Signature Section -->
                ${signatureBlockHtml}
            </div>

            <div>
                <!-- Terms & Conditions -->
                ${termsHtml}

                <!-- Bottom Hospital Address Banner -->
                <div style="border-top: 1px solid #d1d5db; padding-top: 8px; text-align: center;">
                    <div style="font-size: 9.5px; font-weight: bold; color: #374151; letter-spacing: 0.3px; text-transform: uppercase;">
                        ${hospitalInfo.centreName || "GREEN NATUROPATHY"}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}
                    </div>
                    <div style="font-size: 9.5px; color: #4b5563; margin-top: 3px;">
                        📞 +91 ${hospitalInfo.phone || "9345316319"} &nbsp;&bull;&nbsp; ✉️ ${hospitalInfo.email || "greennaturopathycbe@gmail.com"} &nbsp;&bull;&nbsp; 🌐 ${hospitalInfo.websiteDisplay || "greennaturopathy.com"}
                    </div>
                </div>
            </div>
        </div>
    `;
    return htmlString;
}

export async function generateInvoiceCanvas(bill = {}, patient = {}, type = "digital") {
    const htmlString = getInvoiceHtml(bill, patient, type);

    // Create offscreen container directly at top-left with negative z-index
    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.width = "794px";
    container.style.minHeight = "1123px";
    container.style.backgroundColor = "#ffffff";
    container.style.zIndex = "-99999";
    container.style.pointerEvents = "none";
    container.style.boxSizing = "border-box";
    container.innerHTML = htmlString;
    document.body.appendChild(container);

    // Ensure all images are loaded before taking screenshot
    const images = Array.from(container.querySelectorAll("img"));
    if (images.length > 0) {
        await Promise.all(
            images.map(img => img.complete ? Promise.resolve() : new Promise(res => { img.onload = img.onerror = res; }))
        );
    }

    // Wait a brief tick for layout and fonts to render
    await new Promise(r => setTimeout(r, 120));

    const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
        width: 794,
        windowWidth: 794
    });

    if (container.parentNode) {
        container.parentNode.removeChild(container);
    }

    return canvas;
}

export async function downloadBillImage(bill = {}, patient = {}, type = "digital") {
    const canvas = await generateInvoiceCanvas(bill, patient, type);
    const imgData = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    const suffix = type === "signature" ? "_For_Signature" : "";
    link.download = `${bill.invoiceNumber || "Invoice"}${suffix}.png`;
    link.href = imgData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export function printBill(bill = {}, patient = {}, type = "digital") {
    const htmlString = getInvoiceHtml(bill, patient, type);
    const printWindow = window.open("", "_blank", "width=880,height=900");
    if (!printWindow) {
        alert("Please allow popups to print invoice.");
        return;
    }
    printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Invoice - ${bill.invoiceNumber || "Print"}</title>
            <style>
                @page { size: A4; margin: 0; }
                body { margin: 0; padding: 0; background: #ffffff; }
                @media print {
                    body { margin: 0; }
                    .no-print { display: none !important; }
                }
            </style>
        </head>
        <body>
            <div style="display: flex; justify-content: center; padding: 0;">
                ${htmlString}
            </div>
            <script>
                window.onload = function() {
                    window.print();
                    setTimeout(function() { window.close(); }, 500);
                };
            </script>
        </body>
        </html>
    `);
    printWindow.document.close();
}

export async function downloadHtmlInvoicePDF(bill = {}, patient = {}, type = "digital") {
    const canvas = await generateInvoiceCanvas(bill, patient, type);
    const imgData = canvas.toDataURL("image/jpeg", 0.98);
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let renderWidth = pageWidth;
    let renderHeight = (canvas.height * pageWidth) / canvas.width;

    if (renderHeight > pageHeight) {
        const scaleFactor = pageHeight / renderHeight;
        renderWidth *= scaleFactor;
        renderHeight = pageHeight;
        const offsetX = (pageWidth - renderWidth) / 2;
        doc.addImage(imgData, "JPEG", offsetX, 0, renderWidth, renderHeight);
    } else {
        doc.addImage(imgData, "JPEG", 0, 0, renderWidth, renderHeight);
    }

    const suffix = type === "signature" ? "_For_Signature" : "";
    doc.save(`${bill.invoiceNumber || "Invoice"}${suffix}.pdf`);
}

// Fallback Direct jsPDF generator
export function downloadDirectJsPDF(bill = {}, patient = {}, type = "digital") {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;

    // Palette matching user PDF
    const brandGreen = [75, 128, 38];      // #4b8026 Organic Leaf Green
    const grayHeaderBg = [209, 213, 219];  // #d1d5db Header gray
    const grayContentBg = [248, 249, 250]; // #f8fafc Soft card gray
    const grayBorder = [209, 213, 219];    // #d1d5db Box border
    const textDark = [31, 41, 55];         // #1f2937 Charcoal
    const textMuted = [107, 114, 128];     // #6b7280 Muted gray

    // ---------- Top Header: Title on Left, Logo on Right ----------
    // Serif Title "Invoice"
    doc.setFont("times", "bold");
    doc.setFontSize(36);
    doc.setTextColor(...brandGreen);
    doc.text("Invoice", marginX, 54);

    // Official Logo on top right (exact content size: 4.5cm x 2.26cm -> 127.56pt x 64.06pt)
    const logoWidth = 127.56;
    const logoHeight = 64.06;
    const logoX = pageWidth - marginX - logoWidth;
    const logoY = 18;
    if (logoBase64) {
        try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Could not render logo in PDF", e);
        }
    }

    let y = 92;

    // ---------- Patient Details & Invoice Summary (Two side-by-side Gray-capped cards) ----------
    const cardGap = 12;
    const cardWidth = (pageWidth - (marginX * 2) - cardGap) / 2;
    const cardHeight = 78;
    const headerHeight = 17;
    const patientObj = bill.patient || patient || {};

    // --- LEFT CARD: PATIENT DETAILS ---
    const leftCardX = marginX;

    // Header bar
    doc.setFillColor(...grayHeaderBg);
    doc.rect(leftCardX, y, cardWidth, headerHeight, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("PATIENT DETAILS", leftCardX + 8, y + 12);

    // Body container
    doc.setFillColor(...grayContentBg);
    doc.rect(leftCardX, y + headerHeight, cardWidth, cardHeight - headerHeight, "F");
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.rect(leftCardX, y, cardWidth, cardHeight, "S");

    // Details text
    let pY = y + headerHeight + 13;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(17, 24, 39);
    doc.text((patientObj.name || "-").toUpperCase(), leftCardX + 8, pY);

    pY += 11;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(...textDark);
    doc.text(`PATIENT ID: ${patientObj.patientId || bill.patientId || "-"}`, leftCardX + 8, pY);

    pY += 10;
    const ageStr = patientObj.age ? `${patientObj.age}` : "-";
    const genderStr = patientObj.gender || "-";
    doc.text(`AGE / GENDER: ${ageStr} / ${genderStr.toUpperCase()}`, leftCardX + 8, pY);

    pY += 10;
    doc.text(`PHONE: ${patientObj.phone || "-"}`, leftCardX + 8, pY);

    pY += 10;
    const addr = (patientObj.address || "-").replace(/\r?\n|\r/g, ", ");
    const truncatedAddr = addr.length > 38 ? addr.substring(0, 35) + "..." : addr;
    doc.text(`${truncatedAddr.toUpperCase()}`, leftCardX + 8, pY);

    // --- RIGHT CARD: INVOICE SUMMARY ---
    const rightCardX = marginX + cardWidth + cardGap;

    // Header bar
    doc.setFillColor(...grayHeaderBg);
    doc.rect(rightCardX, y, cardWidth, headerHeight, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("INVOICE SUMMARY", rightCardX + 8, y + 12);

    // Body container
    doc.setFillColor(...grayContentBg);
    doc.rect(rightCardX, y + headerHeight, cardWidth, cardHeight - headerHeight, "F");
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.rect(rightCardX, y, cardWidth, cardHeight, "S");

    // Details text
    let bY = y + headerHeight + 13;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.setTextColor(...textDark);
    doc.text(`INVOICE NO: ${bill.invoiceNumber || "-"}`, rightCardX + 8, bY);

    bY += 11;
    doc.text(`INVOICE DATE: ${formatDate(bill.date || bill.createdAt || Date.now())}`, rightCardX + 8, bY);

    bY += 11;
    const docName = bill.doctor || bill.doctorName || "DR. JOTIKA K";
    doc.text(`DOCTOR NAME: ${docName.toUpperCase()}`, rightCardX + 8, bY);

    bY += 11;
    const payMethod = bill.paymentMethod || (bill.payments && bill.payments[0]?.method) || "Cash";
    doc.text(`PAYMENT: ${payMethod.toUpperCase()} (${(bill.status || "PAID").toUpperCase()})`, rightCardX + 8, bY);

    y += cardHeight + 16;

    // ---------- Treatments / Line Items Table (SESSIONS instead of QTY, no empty filler boxes) ----------
    const items = normalizeBillItems(bill);
    const rows = items.map((item) => [
        item.description.toUpperCase(),
        item.quantity,
        currency(item.unitPrice),
        currency(item.total)
    ]);

    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [["DISCRIPTION", "SESSIONS", "UNIT PRICE", "AMOUNT"]],
        body: rows,
        theme: "grid",
        headStyles: {
            fillColor: [229, 231, 235],
            textColor: [55, 65, 81],
            fontStyle: "bold",
            fontSize: 8,
            cellPadding: 5.5,
            lineWidth: 0.5,
            lineColor: [209, 213, 219]
        },
        bodyStyles: {
            fontSize: 8,
            cellPadding: 5.5,
            textColor: [30, 30, 30],
            lineWidth: 0.5,
            lineColor: [209, 213, 219]
        },
        columnStyles: {
            0: { halign: "left" },
            1: { cellWidth: 55, halign: "center" },
            2: { cellWidth: 95, halign: "right" },
            3: { cellWidth: 100, halign: "right" }
        }
    });

    let afterTableY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : y) + 12;

    // ---------- Totals Breakdown (Right) & Amount in Words (Left) ----------
    const totalsWidth = 210;
    const totalsX = pageWidth - marginX - totalsWidth;

    const calculatedItemsTotal = items.reduce((s, i) => s + Number(i.total || 0), 0);
    const subtotal = bill.subtotal !== undefined ? bill.subtotal : (calculatedItemsTotal || Number(bill.totalAmount || 0) + Number(bill.discount || 0));

    // Right Totals Rows
    let tY = afterTableY + 8;
    const printRow = (label, value, isBold = false, textColor = textDark) => {
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setFontSize(isBold ? 9 : 8);
        doc.setTextColor(...textColor);
        doc.text(label, totalsX, tY);
        doc.text(value, pageWidth - marginX, tY, { align: "right" });

        // Underline row
        doc.setDrawColor(220, 225, 230);
        doc.setLineWidth(0.5);
        doc.line(totalsX, tY + 3, pageWidth - marginX, tY + 3);

        tY += 15;
    };

    printRow("SUBTOTAL", currency(subtotal));

    // Only display discount if discount > 0
    if (Number(bill.discount || 0) > 0) {
        printRow("DISCOUNT", currency(bill.discount));
    }

    printRow("TOTAL AMOUNT", currency(bill.totalAmount), true, brandGreen);
    printRow("AMOUNT PAID", currency(bill.amountPaid), true, textDark);

    // Left Side: Amount in Words Enhancement
    const leftWidth = totalsX - marginX - 20;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...textMuted);
    doc.text("TOTAL AMOUNT IN WORDS:", marginX, afterTableY + 8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    const words = numberToWords(Number(bill.totalAmount || 0));
    const splitWords = doc.splitTextToSize(words, leftWidth);
    doc.text(splitWords, marginX, afterTableY + 20);

    const finalY = Math.max(tY + 45, afterTableY + 80);

    // ---------- Signature Section (ONLY IF PHYSICAL SIGNATURE IS REQUESTED) ----------
    // Digital version DOES NOT include signature line
    if (type === "signature") {
        const sigX = pageWidth - marginX - 150;
        doc.setDrawColor(...textMuted);
        doc.setLineWidth(0.75);
        doc.line(sigX, finalY + 25, sigX + 150, finalY + 25);

        doc.setTextColor(...textDark);
        doc.setFontSize(8.5);
        doc.setFont("helvetica", "bold");
        doc.text("Authorized Signatory", sigX + 75, finalY + 38, { align: "center" });
    }

    // ---------- Terms & Conditions ----------
    doc.setTextColor(...textMuted);
    doc.setFontSize(7.2);
    doc.setFont("helvetica", "normal");
    doc.text("Terms & Conditions: 1. All fees are non-refundable once treatments have commenced., 2. Please retain this invoice for your medical records.", marginX, pageHeight - 34);

    // ---------- Bottom Address & Contact Banner ----------
    doc.setFontSize(7.2);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(55, 65, 81);
    const bottomAddress = "GREEN NATUROPATHY: 194-C, SATHY ROAD, NEAR PASUMAI PHARMACY, BHARATHI NAGAR, GOPALAKRISHNAPURAM, GANAPATHY, COIMBATORE, TAMIL NADU 641006";
    doc.text(bottomAddress, pageWidth / 2, pageHeight - 20, { align: "center" });

    const suffix = type === "signature" ? "_For_Signature" : "";
    doc.save(`${bill.invoiceNumber || "Invoice"}${suffix}.pdf`);
}

export async function downloadNewStyleBillPDF(bill = {}, patient = {}, type = "digital") {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
        try {
            await downloadHtmlInvoicePDF(bill, patient, type);
            return;
        } catch (err) {
            console.warn("HTML-to-canvas PDF generation failed, falling back to direct jsPDF:", err);
        }
    }
    return downloadDirectJsPDF(bill, patient, type);
}

// ============================================================================
// 2. CLASSIC INVOICE (PRESERVED SO WE CAN SWITCH BACK AT ANY TIME)
// ============================================================================
export function downloadClassicBillPDF(bill = {}, patient = {}, type = "digital") {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    let y = 0;

    const primaryColor = [115, 167, 66];
    const darkTextColor = [40, 40, 40];
    const lightBgColor = [246, 249, 243];
    const borderLineColor = [218, 230, 206];

    // Top Banner
    const bannerHeight = 84;
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, bannerHeight, "F");

    let textStartX = marginX;
    if (logoBase64) {
        try {
            doc.setFillColor(255, 255, 255);
            doc.roundedRect(marginX, 15, 54, 54, 4, 4, "F");
            doc.addImage(logoBase64, "PNG", marginX + 2, 29, 50, 25.2);
            textStartX = marginX + 66;
        } catch (e) {
            console.warn("Could not render logo in PDF", e);
        }
    }

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(21);
    doc.setFont("helvetica", "bold");
    doc.text((hospitalInfo.name || "GREEN NATUROPATHY").toUpperCase(), textStartX, 40);

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(245, 250, 242);
    doc.text(hospitalInfo.tagline || "Pain Clinic & Holistic Healthcare", textStartX, 57);

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE", pageWidth - marginX, 38, { align: "right" });

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "normal");
    doc.text(`# ${bill.invoiceNumber || "-"}`, pageWidth - marginX, 54, { align: "right" });

    y = 100;

    // Address Bar
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(8.5);
    doc.setFont("helvetica", "normal");
    const addressLine = [hospitalInfo.addressLine1, hospitalInfo.addressLine2].filter(Boolean).join(", ");
    if (addressLine) {
        doc.text(addressLine, marginX, y);
        y += 12;
    }

    doc.setDrawColor(...borderLineColor);
    doc.setLineWidth(1);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 16;

    // Side-by-side cards
    const cardWidth = (pageWidth - (marginX * 2) - 15) / 2;
    const cardHeight = 100;
    const patientObj = bill.patient || patient || {};

    // Left: Patient
    doc.setFillColor(...lightBgColor);
    doc.setDrawColor(...borderLineColor);
    doc.roundedRect(marginX, y, cardWidth, cardHeight, 4, 4, "FD");
    doc.setFillColor(...primaryColor);
    doc.rect(marginX, y, 4, cardHeight, "F");

    let pY = y + 16;
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("PATIENT DETAILS", marginX + 12, pY);

    pY += 14;
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text(`Name: ${patientObj.name || "-"}`, marginX + 12, pY);

    pY += 13;
    doc.setFont("helvetica", "normal");
    doc.text(`Patient ID: ${patientObj.patientId || bill.patientId || "-"}`, marginX + 12, pY);

    pY += 13;
    doc.text(`Phone: ${patientObj.phone || "-"}`, marginX + 12, pY);

    // Right: Invoice
    const rightCardX = marginX + cardWidth + 15;
    doc.setFillColor(...lightBgColor);
    doc.setDrawColor(...borderLineColor);
    doc.roundedRect(rightCardX, y, cardWidth, cardHeight, 4, 4, "FD");
    doc.setFillColor(...primaryColor);
    doc.rect(rightCardX, y, 4, cardHeight, "F");

    let bY = y + 16;
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("INVOICE SUMMARY", rightCardX + 12, bY);

    bY += 14;
    doc.setTextColor(...darkTextColor);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice No: ${bill.invoiceNumber || "-"}`, rightCardX + 12, bY);

    bY += 13;
    doc.text(`Invoice Date: ${formatDate(bill.date || bill.createdAt || Date.now())}`, rightCardX + 12, bY);

    y = y + cardHeight + 20;

    // Table
    const rows = (bill.items || []).map((item, index) => [
        index + 1,
        item.description || item.name || "Treatment / Service",
        item.sessions || item.quantity || 1,
        currency(item.unitPrice || item.rate || 0),
        currency(item.amount || ((item.sessions || item.quantity || 1) * (item.unitPrice || item.rate || 0)))
    ]);

    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [["#", "Description", "Sessions", "Unit Price", "Amount"]],
        body: rows,
        theme: "striped",
        headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
        bodyStyles: { fontSize: 8.5, cellPadding: 6, textColor: darkTextColor }
    });

    let afterTableY = doc.lastAutoTable.finalY + 15;
    const totalsWidth = 220;
    const totalsX = pageWidth - marginX - totalsWidth;
    const calculatedItemsTotal = (bill.items || []).reduce((s, i) => s + Number(i.amount || 0), 0);
    const subtotal = bill.subtotal !== undefined ? bill.subtotal : (calculatedItemsTotal || Number(bill.totalAmount || 0));

    let tY = afterTableY + 14;
    const startTotalsY = tY;

    const printRow = (label, value, isBold = false, textColor = darkTextColor) => {
        doc.setFont("helvetica", isBold ? "bold" : "normal");
        doc.setFontSize(isBold ? 9.5 : 8.5);
        doc.setTextColor(...textColor);
        doc.text(label, totalsX + 12, tY);
        doc.text(value, totalsX + totalsWidth - 12, tY, { align: "right" });
        tY += 16;
    };

    printRow("Subtotal", currency(subtotal));
    if (Number(bill.discount || 0) > 0) printRow("Discount", `- ${currency(bill.discount)}`);
    printRow("Total Amount", currency(bill.totalAmount), true, primaryColor);
    printRow("Amount Paid", currency(bill.amountPaid), true, darkTextColor);

    const totalBoxHeight = tY - afterTableY + 6;
    doc.setFillColor(...lightBgColor);
    doc.setDrawColor(...borderLineColor);
    doc.roundedRect(totalsX, afterTableY, totalsWidth, totalBoxHeight, 4, 4, "FD");

    tY = startTotalsY;
    printRow("Subtotal", currency(subtotal));
    if (Number(bill.discount || 0) > 0) printRow("Discount", `- ${currency(bill.discount)}`);
    printRow("Total Amount", currency(bill.totalAmount), true, primaryColor);
    printRow("Amount Paid", currency(bill.amountPaid), true, darkTextColor);

    // Left Amount in Words Box
    const leftBoxWidth = totalsX - marginX - 15;
    doc.setFillColor(...lightBgColor);
    doc.setDrawColor(...borderLineColor);
    doc.roundedRect(marginX, afterTableY, leftBoxWidth, totalBoxHeight, 4, 4, "FD");
    doc.setFillColor(...primaryColor);
    doc.rect(marginX, afterTableY, 4, totalBoxHeight, "F");

    doc.setTextColor(...primaryColor);
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.text("TOTAL AMOUNT IN WORDS", marginX + 14, afterTableY + 16);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...darkTextColor);
    const splitWords = doc.splitTextToSize(numberToWords(Number(bill.totalAmount || 0)), leftBoxWidth - 28);
    doc.text(splitWords, marginX + 14, afterTableY + 32);

    // Bottom Footer
    doc.setFillColor(...primaryColor);
    doc.rect(0, pageHeight - 20, pageWidth, 20, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text("Thank you for choosing Green Naturopathy. Get well soon!", pageWidth / 2, pageHeight - 7, { align: "center" });

    const suffix = type === "signature" ? "_For_Signature" : "";
    doc.save(`${bill.invoiceNumber || "Invoice"}_Classic${suffix}.pdf`);
}

// ============================================================================
// PRIMARY DOWNLOAD FUNCTION (USES NEW MATCHING PDF STYLE)
// ============================================================================
export function downloadBillPDF(bill = {}, patient = {}, type = "digital") {
    return downloadNewStyleBillPDF(bill, patient, type);
}

export default downloadBillPDF;
